import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  UserProfileApplication,
  APPLICATION_SOURCE,
  APPLICATION_STATUS,
} from "@booyaltd/core";
import {
  createApplication,
  submitApplication,
  updateApplicationEmailDetails,
  updateApplicationPhoneDetails,
  verifyApplicationEmailDetails,
  verifyApplicationPhoneDetails,
} from "../api/client";
import { buildDeviceDetails } from "../utils/device";
import { getAttributionTouches } from "../utils/attribution";
import { v4 as uuidv4 } from "uuid";
import { AccountContext } from "./AccountContext";
import {
  MEMBER_STATUS_STANDARD,
  MEMBER_STATUS_TRIAL,
} from "../api/entities/UserProfile";
import useLogger from "../hooks/useLogger";
import {
  getUserMessage,
  getPhoneValidationSuggestion,
  PhoneValidationSuggestion,
} from "../utils/errorMessages";

export type InitialiseParams = {
  route: "try" | "buy" | "trybuy" | "giftcard" | undefined;
} & (
  | { source: APPLICATION_SOURCE.PUBLIC; code?: string; page: string }
  | { source: APPLICATION_SOURCE.GIFTCARD; code?: string; page: string }
  | {
      source: APPLICATION_SOURCE.REFERRAL;
      code: string;
      page: string;
    }
);

export enum Step {
  giftcard,
  loading,
  "phone-input-verify",
  "input-details",
  purchase,
  success,
  // #1245: the free sign-up's last screen (route "try"). Last, so no existing
  // step's number changes.
  "get-app",
}

type StepLabel = { steps: Array<Step>; label: string };

type OnboardingContextType = {
  initialise: ({ source, code }: InitialiseParams) => Promise<void> | void;
  runWithLoader: (
    action: () => Promise<void>,
    force?: boolean,
    errorContext?: string,
  ) => void;
  application?: UserProfileApplication;
  stepLabels: StepLabel[];
  activeStep: Step;
  loading: boolean;
  error?: string;
  resetError(): void;
  phoneSuggestion?: PhoneValidationSuggestion;
  resetPhoneSuggestion(): void;
  setPhone(
    countryCode: string,
    phoneNumber: string,
    // sendMethod?: "sms" | "whatsapp",
    captchaToken: string,
  ): void;
  verifyPhone(code: string): void;
  setDetails(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ): void;
  verifyEmail(code: string): void;
  submit(): void;
};

const OnboardingContext = createContext<OnboardingContextType>({
  initialise: () => {},
  runWithLoader: () => {},
  application: undefined,
  stepLabels: [],
  activeStep: Step.loading,
  loading: true,
  error: undefined,
  resetError: () => {},
  phoneSuggestion: undefined,
  resetPhoneSuggestion: () => {},
  setPhone: () => {},
  verifyPhone: () => {},
  setDetails: () => {},
  verifyEmail: () => {},
  submit: () => {},
});

const OnboardingProvider = (props: PropsWithChildren<InitialiseParams>) => {
  const { logMetricEvent } = useLogger();
  const { userProfile, storeAuthResponse, validateLogin } =
    useContext(AccountContext);
  const [source, setSource] = useState<APPLICATION_SOURCE>();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>();
  const [phoneSuggestion, setPhoneSuggestion] = useState<
    PhoneValidationSuggestion | undefined
  >();
  const [application, setApplication] = useState<UserProfileApplication>();

  const runWithLoader = useCallback(
    (
      action: () => Promise<void>,
      force: boolean = false,
      errorContext?: string,
    ) => {
      if (!force && loading) return;

      setError(undefined);
      setLoading(true);
      action()
        .catch((e) => {
          setError(getUserMessage(e, errorContext));
        })
        .finally(() => setLoading(false));
    },
    [loading],
  );

  const initialise = useCallback(
    ({ source, code, page }: InitialiseParams) => {
      setSource(source);

      if (
        source !== APPLICATION_SOURCE.GIFTCARD ||
        (source === APPLICATION_SOURCE.GIFTCARD && typeof code === "string")
      ) {
        runWithLoader(async () => {
          // #660: attribution rides create as well as submit — an existing
          // member redeeming a giftcard converts inline server-side here and
          // never reaches submit; it also stamps abandoned applications.
          const attribution = getAttributionTouches();
          setApplication(
            await createApplication({
              source,
              code,
              page,
              ...(attribution ? { attribution } : {}),
            }),
          );
        });
      }
    },
    [runWithLoader],
  );

  const resetError = useCallback(() => {
    setError(undefined);
  }, [setError]);

  const resetPhoneSuggestion = useCallback(() => {
    setPhoneSuggestion(undefined);
  }, []);

  // setPhone handles errors locally (rather than via runWithLoader) so we
  // can extract the structured phone-validation suggestion from #284's
  // server response and surface it alongside the user-facing message.
  const setPhone = useCallback(
    async (countryCode: string, phoneNumber: string, captcha: string) => {
      if (!application) return;
      if (loading) return;

      setError(undefined);
      setPhoneSuggestion(undefined);
      setLoading(true);
      try {
        setApplication(
          await updateApplicationPhoneDetails(application.id, {
            method: "sms",
            countryCode,
            phoneNumber,
            captcha,
          }),
        );
        logMetricEvent({
          type: "sign_up",
          payload: { source: application.source, step: "phone-submitted" },
        });
      } catch (e) {
        setError(getUserMessage(e, "phone-send"));
        const suggestion = getPhoneValidationSuggestion(e);
        if (suggestion) setPhoneSuggestion(suggestion);
      } finally {
        setLoading(false);
      }
    },
    [application, loading, logMetricEvent],
  );

  const verifyPhone = useCallback(
    async (code: string) => {
      if (!application) return;

      runWithLoader(
        async () => {
          setApplication(
            await verifyApplicationPhoneDetails(application.id, { code }),
          );
          logMetricEvent({
            type: "sign_up",
            payload: { source: application.source, step: "phone-verified" },
          });
        },
        false,
        "phone-verify",
      );
    },
    [application, runWithLoader, logMetricEvent],
  );

  const setDetails = useCallback(
    async (
      firstName: string,
      lastName: string,
      email: string,
      password: string,
    ) => {
      if (!application) return;

      runWithLoader(async () => {
        setApplication(
          await updateApplicationEmailDetails(application.id, {
            firstName,
            lastName,
            email,
            password,
          }),
        );
        logMetricEvent({
          type: "sign_up",
          payload: { source: application.source, step: "details-submitted" },
        });
      });
    },
    [application, runWithLoader, logMetricEvent],
  );

  const verifyEmail = useCallback(
    async (code: string) => {
      if (!application) return;

      runWithLoader(async () => {
        setApplication(
          await verifyApplicationEmailDetails(application.id, { code }),
        );
        logMetricEvent({
          type: "sign_up",
          payload: { source: application.source, step: "email-verified" },
        });
      });
    },
    [application, runWithLoader, logMetricEvent],
  );

  const submit = useCallback(() => {
    if (!application || submitted) return;

    setSubmitted(true);

    runWithLoader(async () => {
      // #660 + #659: the request carries the stored marketing attribution
      // (persisted server-side on the application row) and the pixel eventId
      // minted here so the browser event below shares it with the server.
      const eventId = uuidv4();
      const attribution = getAttributionTouches();

      const {
        application: updatedApplication,
        authToken,
        refreshToken,
      } = await submitApplication(application.id, {
        device: await buildDeviceDetails(),
        eventId,
        ...(attribution ? { attribution } : {}),
      });

      // Funnel step (#577): fire the application-submitted step for every
      // completed submission so the funnel is not truncated for returning
      // applicants. `newAccount` distinguishes a genuine net-new signup from
      // a returning applicant re-applying (previously the only gate on this
      // event firing at all).
      logMetricEvent(
        {
          type: "sign_up",
          payload: {
            source: application.source,
            step: "application-submitted",
            newAccount: !application.phoneExists && !application.emailExists,
          },
        },
        eventId,
      );

      if (authToken && refreshToken) {
        await storeAuthResponse({ authToken, refreshToken });
      }

      await validateLogin();

      setApplication(updatedApplication);
    });
  }, [
    submitted,
    application,
    runWithLoader,
    storeAuthResponse,
    validateLogin,
    logMetricEvent,
  ]);

  const stepLabels = useMemo((): StepLabel[] => {
    if (source === APPLICATION_SOURCE.GIFTCARD) {
      return [
        {
          steps: [Step["giftcard"]],
          label: "Giftcard",
        },
        {
          steps: [Step["phone-input-verify"]],
          label: "Phone",
        },
        {
          steps: [Step["input-details"]],
          label: "Login Details",
        },
      ];
    }

    if (!application) return [{ steps: [Step.loading], label: "" }];

    return [
      {
        steps: [Step["phone-input-verify"]],
        label: "Phone",
      },
      {
        steps: [Step["input-details"]],
        label: "Login Details",
      },
      props.route === "try"
        ? { steps: [Step["get-app"]], label: "Get the app" }
        : {
            steps: [Step.purchase],
            label: props.route === "buy" ? "Purchase" : "Trial Ready",
          },
    ];
  }, [source, application, props.route]);

  const activeStep = useMemo<Step>(() => {
    if (!application && source === APPLICATION_SOURCE.GIFTCARD.valueOf())
      return Step.giftcard;

    if (!application) return Step.loading;

    const { status } = application;

    switch (status.toString()) {
      case APPLICATION_STATUS.CREATED.toString():
        return Step["phone-input-verify"];
      case APPLICATION_STATUS.PHONE_VERIFIED.toString():
        return Step["input-details"];
      case APPLICATION_STATUS.DETAILS_VERIFIED.toString():
      case APPLICATION_STATUS.CONVERTED.toString():
        if (!userProfile) return Step.loading;

        if (
          userProfile.memberStatusId === MEMBER_STATUS_STANDARD ||
          userProfile.memberStatusId === MEMBER_STATUS_TRIAL
        ) {
          return Step["success"];
        }

        // #1245: the free sign-up (/try, /sign-up, /free-trial) has no
        // purchase step; it ends on "Get the app". /buy, gift-card and
        // referral sign-ups keep today's purchase step.
        if (props.route === "try") {
          return Step["get-app"];
        }

        return Step["purchase"];
      default:
    }

    return Step.loading;
  }, [application, source, userProfile, props.route]);

  // Only run on mount
  useEffect(() => {
    initialise(props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !submitted &&
      !loading &&
      application &&
      application.status.toString() ===
        APPLICATION_STATUS.DETAILS_VERIFIED.toString()
    ) {
      submit();
    }
  }, [loading, application, props, submit, submitted]);

  const context: OnboardingContextType = {
    initialise,
    runWithLoader,
    application,
    stepLabels,
    activeStep,
    loading,
    error,
    resetError,
    phoneSuggestion,
    resetPhoneSuggestion,
    setPhone,
    verifyPhone,
    setDetails,
    verifyEmail,
    submit,
  };

  return (
    <OnboardingContext.Provider value={context}>
      {props.children}
    </OnboardingContext.Provider>
  );
};

export { OnboardingContext, OnboardingProvider };
